import type { PageMetadata } from "@moment/api-collab/api-types";

import { generateUniqueSlug, sluggify } from "../../internal/page-metadata/page-metadata.utils";

const defaultPageFilename = (pageType: PageMetadata["type"] | undefined): string => {
	if (pageType === "request") {
		return "index.yml";
	}

	if (pageType === "canvas") {
		return "index.json";
	}

	return "index.md";
};

export const getCurrentReorderPagePath = (
	_pages: PageMetadata[],
	page: Pick<PageMetadata, "id" | "path" | "type" | "slug" | "parentPageID">
): string => page.path ?? `pages/${page.id}/${defaultPageFilename(page.type)}`;

export const getTemporaryReorderPagePath = (
	pageId: string,
	currentPath?: string,
	pageType?: PageMetadata["type"]
): string => {
	const filename = currentPath?.split("/").pop() || defaultPageFilename(pageType);
	return `pages/__reorder__${pageId}/${filename}`;
};

export const getTitleBasedPageSlug = (
	page: Pick<PageMetadata, "id" | "title" | "parentPageID">,
	pages: PageMetadata[]
): string => {
	const baseSlug = sluggify(page.title || "") || page.id;
	return generateUniqueSlug(
		baseSlug,
		page.id,
		page.parentPageID,
		pages.map((candidate) =>
			candidate.id === page.id ? { ...candidate, slug: baseSlug } : candidate
		)
	);
};

export const resolveConflictingIndexSlugs = (pages: PageMetadata[]): PageMetadata[] => {
	const resolvedPages = pages.map((page) => ({ ...page }));
	const parentsWithIndexSlug = new Set<string>();

	for (const page of resolvedPages) {
		if (page.slug !== "index") {
			continue;
		}

		const parentKey = page.parentPageID ?? "__root__";
		if (parentsWithIndexSlug.has(parentKey)) {
			page.slug = getTitleBasedPageSlug(page, resolvedPages);
			continue;
		}

		parentsWithIndexSlug.add(parentKey);
	}

	return resolvedPages;
};

export const isPageIdUnique = (
	id: string,
	currentPageId: string,
	pages: Array<Pick<PageMetadata, "id">>,
	ignoredPageIds: string[] = []
): boolean => {
	const ignoredIds = new Set(ignoredPageIds);
	return !pages.some(
		(page) => page.id === id && page.id !== currentPageId && !ignoredIds.has(page.id)
	);
};
